import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { selectUser } from '../../auth/authSlice';
import {
  selectAllCachedMembers,
  selectGroupDetail,
  loadGroupDetail,
} from '../../sharing/groupsSlice';
import {
  acknowledgeRecall,
  loadCustodyHistory,
  remindCustodian,
  returnItem,
  selectCustodyEvents,
  selectCustodyHistory,
  selectCustodyLoading,
} from '../custodySlice';
// PR9 / LD1 — share-claim "Mark returned" routes to the share-
// management endpoint (PATCH /users/shares/{id}, action='RETURN')
// instead of /inventory/{id}/return. BE#171 rejects /return when the
// row has no preceding HANDOFF event, which is exactly the case for
// share-claim duplicates.
import {
  manageShare,
  selectInboundLifecycleShares,
  selectOutboundLifecycleShares,
} from '../../sharing/sharingSlice';
import type {
  CustodyEventSummary,
  GroupMember,
} from '../../../api/types';
// HandOffSheet (owner-push) intentionally NOT imported — the feature is
// hidden (2026-06-08); Borrow (member-pull) covers item transfers. The
// component + handoffItem thunk remain dormant; re-import to restore.
import { RequestHandoffSheet } from './RequestHandoffSheet';
import { RecallSheet } from './RecallSheet';

/**
 * CustodySection — Sprint 3 / Chunk 10A · FE-10.4.
 *
 * Drop-in section for ItemDetailScreen that surfaces:
 *
 *   1. A "Currently with …" banner whose copy adapts to four states:
 *      - viewer is custodian + owner   → "Currently with you"
 *      - viewer is custodian, not owner → "Borrowed from {owner}, due {date}"
 *      - viewer is owner, item is out  → "With {holder}, due {date}" (red if overdue)
 *      - no group context yet          → render nothing (item not in a crew)
 *
 *   2. Quick-action row keyed off of who-is-who:
 *      - viewer is owner with item → [Hand off]
 *      - viewer is holder, not owner → [Hand back to owner]
 *
 *   3. A collapsible custody history timeline (last 5 events visible,
 *      tap to expand the rest). Lazy-fetches the history when the user
 *      first opens the screen so we don't pay the network roundtrip
 *      for items that are never inspected.
 *
 * Data source: `selectCustodyHistory(itemId)` — populated by
 * `loadCustodyHistory` and kept fresh by the cross-slice listener that
 * appends events on each successful handoff/return.
 */

interface CustodySectionProps {
  itemId: number;
  itemName: string;
  ownerUserId: number;
  /** FE-#133 — Parent wires this to open its AddItemToGroupSheet.
   *  HandOffSheet exposes a CTA in its empty state ("Add to a crew")
   *  that fires this callback so the user can bind the item to a
   *  crew with members in one move instead of staring at a
   *  dead-end. Optional — sheet falls back to plain copy without it. */
  onOpenAddToGroup?: () => void;
  /** When the item is bound to one or more groups, the primary group
   *  context is passed here so the HandOffSheet can pre-fill `group_id`
   *  + load that group's members. Optional — without a group, the
   *  Hand off button is hidden. */
  primaryGroupId?: number | null;
  /** Inline custody snapshot from the inventory record. Used as a
   *  fallback before the full history hydrates. */
  initialCustodianUserId?: number | null;
  initialCustodyDueDate?: string | null;
  /** FE #155 — when a recall is already active on this item, the
   *  CustodySection should disable both Send Reminder + Recall rows
   *  on the owner's view + change the copy to "Recall pending —
   *  awaiting {custodian}". Prevents the owner from spamming
   *  high-priority emails. Server already rate-limits but the
   *  UX needs the pending state regardless. */
  hasActiveRecall?: boolean;
  /** BE#160 — share-claim attribution. When the row is a share-claim
   *  duplicate (i.e. the viewer owns this row but received it via
   *  /share/{token} from another user), these point back at the
   *  original lender + share record. Drives:
   *   - "Borrowed from {lender} via share" banner copy
   *   - "Mark returned" CTA that calls PATCH /users/shares/{id}
   *     instead of POST /inventory/{id}/return (BE#171 rejects
   *     /return calls without a preceding HANDOFF event).
   *  Null on items the viewer scanned themselves. */
  claimedFromUserId?: number | null;
  claimedFromShareId?: number | null;
  /** FE-248 v2 follow-up — borrower view fallback. When the viewer is
   *  the custodian-but-not-owner and the cached group member list
   *  doesn't yet have the owner's user record (async race, or the
   *  item is hydrated from a stale cache), use this name in the
   *  banner ("Borrowed from {fallbackOwnerName}") so the user isn't
   *  staring at a generic "Borrowed from owner". Sourced server-side
   *  from `GroupItemSummary.owner_name`. Null when unknown. */
  fallbackOwnerName?: string | null;
}

const PREVIEW_COUNT = 5;

function formatDueLabel(dueIso: string | null | undefined): {
  text: string;
  overdue: boolean;
  dueSoon: boolean;
} {
  if (!dueIso) return { text: '', overdue: false, dueSoon: false };
  const due = new Date(dueIso);
  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffMs < 0) {
    const overBy = Math.abs(diffDays);
    return {
      text: overBy === 0 ? 'Overdue' : `Overdue by ${overBy} day${overBy === 1 ? '' : 's'}`,
      overdue: true,
      dueSoon: false,
    };
  }
  if (diffDays === 0) {
    return { text: 'Due today', overdue: false, dueSoon: true };
  }
  if (diffDays === 1) {
    return { text: 'Due tomorrow', overdue: false, dueSoon: true };
  }
  return {
    text: `Due in ${diffDays} day${diffDays === 1 ? '' : 's'}`,
    overdue: false,
    dueSoon: diffDays <= 3,
  };
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * PR17 — Absolute timestamp with friendly grouping. Mirrors
 * `formatTimestamp` in LocationHistoryTimeline so both timelines
 * read the same way:
 *   - today    → "Today, 11:51 AM"
 *   - yesterday→ "Yesterday, 4:42 PM"
 *   - older    → "May 13 · 11:51 AM"
 */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const time = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    if (sameDay) return `Today, ${time}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (
      d.getFullYear() === yesterday.getFullYear() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getDate() === yesterday.getDate()
    ) {
      return `Yesterday, ${time}`;
    }
    return `${d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })} · ${time}`;
  } catch {
    return iso;
  }
}

/**
 * PR17 — Per-event visual descriptor for the redesigned timeline.
 * Maps every synthetic + real action to (icon, uppercase source
 * label, tint color). Parallels `describeSource` in the Location
 * History timeline so the two surfaces read with the same vocabulary.
 */
function describeCustodyAction(event: CustodyEventSummary): {
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  tint: string;
} {
  const id = (event as any).id as number;
  // Synthetic share-lifecycle events keep their dedicated visual
  // language (PR9.1/PR10/PR13 ids). Tint distinguishes direction:
  // borrower-side primary, lender-side tertiary, confirms = green.
  if (id === -1) {
    return { icon: 'south-west', label: 'Claimed via share', tint: fpColors.primary };
  }
  if (id === -2) {
    return { icon: 'schedule-send', label: 'Marked returned', tint: fpColors.tertiary };
  }
  if (id === -3) {
    return { icon: 'check-circle', label: 'Receipt confirmed', tint: fpColors.primary };
  }
  if (id === -11) {
    return { icon: 'north-east', label: 'Lent via share', tint: fpColors.tertiary };
  }
  if (id === -12) {
    return { icon: 'schedule-send', label: 'Borrower marked returned', tint: fpColors.tertiary };
  }
  if (id === -13) {
    return { icon: 'check-circle', label: 'Receipt confirmed', tint: fpColors.primary };
  }
  // Real BE event — fall back to the action enum.
  switch (event.action) {
    case 'HANDOFF':
      return { icon: 'arrow-forward', label: 'Handed off', tint: fpColors.primary };
    case 'LEND_OUT':
      return { icon: 'north-east', label: 'Lent out', tint: fpColors.tertiary };
    case 'RETURN':
      return { icon: 'check-circle', label: 'Returned', tint: fpColors.primary };
    case 'LEND_BACK':
      return { icon: 'check-circle', label: 'Returned (external)', tint: fpColors.primary };
    case 'RECONCILE':
      return { icon: 'fact-check', label: 'Reconciled', tint: fpColors.primary };
    case 'RECALL':
      return { icon: 'priority-high', label: 'Recalled', tint: fpColors.error };
    case 'REMIND':
      return { icon: 'notifications', label: 'Reminder sent', tint: fpColors.outline };
    case 'RESET':
      return { icon: 'refresh', label: 'Custody reset', tint: fpColors.outline };
    default:
      return { icon: 'history', label: String(event.action), tint: fpColors.outline };
  }
}

function actionLabel(event: CustodyEventSummary): string {
  // PR9 + PR10 + PR13 — synthetic share-lifecycle events.
  //   id = -1  → borrower claim       ('Claimed via share')
  //   id = -2  → borrower returned    ('Marked returned')
  //   id = -3  → lender confirmed     ('Receipt confirmed by lender')
  //   id = -11 → lender lent out      ('Lent via share')
  //   id = -12 → borrower returned    ('Borrower marked returned')
  //   id = -13 → lender confirmed     ('You confirmed receipt')
  // Real CustodyEventSummary rows never have id < 0.
  const id = (event as any).id;
  if (id === -1 && event.note === 'Claimed via share') {
    return 'Claimed via share';
  }
  if (id === -2 && event.note === 'Marked returned') {
    return 'Marked returned';
  }
  if (id === -3 && event.note === 'Receipt confirmed by lender') {
    return 'Receipt confirmed';
  }
  if (id === -11 && event.note === 'Lent via share') {
    return 'Lent via share';
  }
  if (id === -12 && event.note === 'Borrower marked returned') {
    return 'Borrower marked returned';
  }
  if (id === -13 && event.note === 'You confirmed receipt') {
    return 'You confirmed receipt';
  }
  switch (event.action) {
    case 'HANDOFF':
      return 'Handed off';
    case 'RETURN':
      return 'Returned';
    case 'RECONCILE':
      return 'Reconciled';
    case 'RESET':
      return 'Custody reset';
    case 'LEND_OUT':
      return 'Lent out';
    case 'LEND_BACK':
      return 'Returned (external)';
    case 'RECALL':
      return 'Recalled';
    case 'REMIND':
      return 'Reminder sent';
    default:
      return String(event.action);
  }
}

function actionIcon(event: CustodyEventSummary): keyof typeof MaterialIcons.glyphMap {
  // PR9 + PR10 + PR13 — synthetic share-lifecycle icons.
  // Distinct from crew-handoff icons so the user reads at-a-glance
  // that these events came via the share-link flow. Direction:
  //   borrower-side  → south-west (item coming in)
  //   lender-side    → north-east (item going out)
  const id = (event as any).id;
  if (id === -1 && event.note === 'Claimed via share') {
    return 'south-west';
  }
  if (id === -2 && event.note === 'Marked returned') {
    return 'schedule-send';
  }
  if (id === -3 && event.note === 'Receipt confirmed by lender') {
    return 'check-circle';
  }
  if (id === -11 && event.note === 'Lent via share') {
    return 'north-east';
  }
  if (id === -12 && event.note === 'Borrower marked returned') {
    return 'schedule-send';
  }
  if (id === -13 && event.note === 'You confirmed receipt') {
    return 'check-circle';
  }
  switch (event.action) {
    case 'HANDOFF':
    case 'LEND_OUT':
      return 'arrow-forward';
    case 'RETURN':
    case 'LEND_BACK':
      return 'check-circle';
    case 'RECALL':
      return 'priority-high';
    case 'REMIND':
      return 'notifications';
    case 'RECONCILE':
      return 'fact-check';
    default:
      return 'history';
  }
}

function memberLabel(m: GroupMember | undefined): string {
  if (!m) return '';
  if (m.name && m.name.trim()) return m.name;
  if (m.email && m.email.trim()) return m.email.split('@')[0];
  if (m.invitee_email && m.invitee_email.trim()) return m.invitee_email.split('@')[0];
  return `User #${m.user_id ?? '?'}`;
}

/**
 * PR9.2 — extract a usable email address for the user the "row is
 * pointing at" so the FE can render a small subtitle + make rows
 * tappable to mailto. Returns null when neither `email` nor
 * `invitee_email` is populated (a freshly-invited crew member who
 * hasn't accepted yet, for example). */
function memberEmail(m: GroupMember | undefined): string | null {
  if (!m) return null;
  const direct = m.email?.trim();
  if (direct) return direct;
  const invitee = m.invitee_email?.trim();
  if (invitee) return invitee;
  return null;
}

/** Open the platform mail composer with a prefilled to-address.
 *  Wrapped so the FE has one place to swap behavior later (e.g.
 *  in-app messaging, contact card modal). */
async function openMailto(email: string): Promise<void> {
  const url = `mailto:${email}`;
  try {
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
    } else {
      Toast.show({
        type: 'info',
        text1: 'No mail app set up',
        text2: email,
      });
    }
  } catch {
    Toast.show({
      type: 'info',
      text1: 'Email',
      text2: email,
    });
  }
}

export const CustodySection: React.FC<CustodySectionProps> = ({
  itemId,
  itemName,
  ownerUserId,
  primaryGroupId,
  hasActiveRecall = false,
  initialCustodianUserId,
  initialCustodyDueDate,
  claimedFromUserId,
  claimedFromShareId,
  fallbackOwnerName,
  onOpenAddToGroup,
}) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const history = useAppSelector(selectCustodyHistory(itemId));
  const events = useAppSelector(selectCustodyEvents(itemId));
  const loading = useAppSelector(selectCustodyLoading(itemId));
  const groupDetail = useAppSelector(selectGroupDetail(primaryGroupId ?? null));
  // PR10 — broaden from CLAIMED-only (PR9.1) to the full return
  // lifecycle (CLAIMED → RETURNED → COMPLETED). Without this, the
  // moment the borrower taps Mark returned the share flips to
  // RETURNED and matchedShare becomes null, dropping all share
  // context (lender name, banner copy, synthetic events). Now the
  // share survives all the way to COMPLETED so we can render the
  // "Returned · awaiting confirmation" banner + "Completed" history.
  const inboundShares = useAppSelector(selectInboundLifecycleShares);
  const matchedShare = useMemo(() => {
    if (claimedFromShareId == null) return null;
    return (
      inboundShares.find((s) => s.share_id === claimedFromShareId) ?? null
    );
  }, [inboundShares, claimedFromShareId]);

  // PR12 — lender-side outbound share matching. When the viewer is
  // the owner of an item they previously shared via link, and the
  // borrower has marked returned, the share row sits in RETURNED
  // status waiting for the lender's "Confirm receipt" tap.
  //
  // PR14 / BE#179 — match by `item_id` (now exposed on ShareListItem).
  // Falls back to `item_name` for any legacy rows where `item_id` is
  // null (e.g. backing inventory was deleted before BE#179 shipped).
  // The fallback also covers a brief window where BE#179 may be live
  // but a particular row hasn't been re-serialized — defensive.
  //
  // Two scopes exposed:
  //   matchedOutboundShare         — CLAIMED + RETURNED only. Drives
  //                                   PR12's banner + confirm-receipt
  //                                   + possession-action suppression.
  //   matchedOutboundShareLifecycle — adds COMPLETED. PR13's custody
  //                                   history needs the COMPLETED row
  //                                   so the "Receipt confirmed"
  //                                   synthetic event still renders.
  const outboundShares = useAppSelector(selectOutboundLifecycleShares);
  const matchedOutboundShareLifecycle = useMemo(() => {
    // Prefer id match (precise, survives renames). Fall back to
    // item_name only when id is null on the row.
    return (
      outboundShares.find((s) => s.item_id != null && s.item_id === itemId) ??
      outboundShares.find(
        (s) => s.item_id == null && s.item_name === itemName,
      ) ??
      null
    );
  }, [outboundShares, itemId, itemName]);
  const matchedOutboundShare = useMemo(() => {
    return (
      matchedOutboundShareLifecycle &&
      (matchedOutboundShareLifecycle.status === 'CLAIMED' ||
        matchedOutboundShareLifecycle.status === 'RETURNED')
        ? matchedOutboundShareLifecycle
        : null
    );
  }, [matchedOutboundShareLifecycle]);

  const [expanded, setExpanded] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);

  // Hydrate history on mount.
  useEffect(() => {
    if (itemId) {
      dispatch(loadCustodyHistory(itemId));
    }
  }, [dispatch, itemId]);

  // Hydrate the group detail (so we can resolve user_id → name in the
  // banner + history rows).
  useEffect(() => {
    if (primaryGroupId && !groupDetail) {
      dispatch(loadGroupDetail(primaryGroupId));
    }
  }, [dispatch, primaryGroupId, groupDetail]);

  // FE-248 v2 follow-up — broadened name lookup. v1 only scanned
  // `groupDetail?.members` for the route's primaryGroupId, which
  // missed cross-group cases (e.g. opening a borrowed item from the
  // Items page where no group context is in the route, OR an item
  // bound to multiple groups where the relevant member is in group
  // B but the route's group context is A). Now we union the
  // primary-group members with EVERY cached group's members.
  // primaryGroupId members take priority (their record is most
  // likely freshest given the route), then global fallback fills
  // in. Either source resolves to the same display name when both
  // have the user.
  const globalMembers = useAppSelector(selectAllCachedMembers);
  const memberByUserId = useMemo<Record<number, GroupMember>>(() => {
    const out: Record<number, GroupMember> = { ...globalMembers };
    for (const m of groupDetail?.members ?? []) {
      if (m.user_id != null) out[m.user_id] = m;
    }
    return out;
  }, [groupDetail, globalMembers]);

  const custodianUserId =
    history?.current_custodian_user_id ?? initialCustodianUserId ?? ownerUserId;
  const custodyDueDate =
    history?.custody_due_date ?? initialCustodyDueDate ?? null;

  const viewerIsOwner = !!currentUser && currentUser.id === ownerUserId;
  const viewerIsCustodian = !!currentUser && currentUser.id === custodianUserId;
  // Sprint 8 Phase E (Issue 4a fix) — items that never had a custody
  // handoff have `custodianUserId == null`. Treat that as "item is
  // still with the owner" so the owner viewing their own item lands
  // in the "Currently with you" branch instead of falling through to
  // the confusing "With another member" copy.
  const itemWithOwner =
    custodianUserId == null || custodianUserId === ownerUserId;

  // PR9 / LD1 — share-claim duplicate detection. During the transition
  // away from the duplicate-row model, an item that the viewer "owns"
  // (technically user_id == viewer) but was acquired via /share/{token}
  // is conceptually borrowed, not owned. The presence of
  // `claimed_from_share_id` is the canonical server signal — set by
  // BE#160, never null on share-claim rows. This takes precedence over
  // the owner/custodian branching below: even if the viewer is both
  // owner AND custodian of the duplicate, the banner should read
  // "Borrowed from {lender} via share" and the action should be
  // "Mark returned" (PATCH /users/shares/{id}) NOT "Hand back"
  // (POST /inventory/{id}/return, which BE#171 rejects for share-claim
  // rows because there's no preceding HANDOFF event).
  const isShareClaimBorrower =
    !!currentUser &&
    claimedFromShareId != null &&
    claimedFromUserId != null &&
    claimedFromUserId !== currentUser.id;

  const dueInfo = formatDueLabel(custodyDueDate);

  /* ── banner ───────────────────────────────────────────────────────── */
  let bannerIcon: keyof typeof MaterialIcons.glyphMap = 'check-circle';
  let bannerTone: 'neutral' | 'soon' | 'overdue' = 'neutral';
  let bannerTitle = 'Currently with you';
  let bannerSubtitle = '';
  // PR9.2 — track which user the banner is "about" (the counterparty)
  // so we can surface their email + a tappable mailto right on the
  // banner. Null when banner is about the viewer themselves
  // (e.g. "Currently with you" — nobody else to contact).
  let bannerCounterpartyUserId: number | null = null;

  // PR10 — share-claim has THREE sub-states (CLAIMED / RETURNED /
  // COMPLETED). Surface each one so the banner reflects where the
  // return flow actually is. Default to 'CLAIMED' when the matched
  // share hasn't hydrated yet so the optimistic UX still works.
  const shareStatus = matchedShare?.status ?? 'CLAIMED';

  if (isShareClaimBorrower) {
    // PR9 / LD1 — share-claim duplicate. Even though the DB says the
    // viewer "owns" this row (a transitional artifact of the old
    // duplicate-row model), the user mental model is "I borrowed this
    // via a link." Use claimed_from_user_id to name the lender and
    // route the Hand-back to the share-management endpoint.
    //
    // Lender name resolution (3-step fallback):
    //   1. cached crew member (rare for share-claim — usually the
    //      lender is NOT in any of the borrower's crews)
    //   2. matched share row's `shared_by` string ("Name (email)") —
    //      authoritative when /users/shares has been hydrated
    //   3. literal "another user" — only when both caches are cold
    const lenderMember = memberByUserId[claimedFromUserId as number];
    const lenderName =
      (lenderMember ? memberLabel(lenderMember) : null) ??
      (matchedShare?.shared_by
        ? matchedShare.shared_by.split('(')[0].trim() || null
        : null);

    if (shareStatus === 'RETURNED') {
      // PR10 Bug 2 — borrower has marked returned, awaiting lender
      // to tap "Confirm receipt" (the email button, or the in-app
      // CTA from PR12). Banner becomes informational, not actionable.
      bannerTitle = lenderName
        ? `Returned to ${lenderName}`
        : 'Returned';
      bannerSubtitle = 'Awaiting confirmation from lender';
      bannerIcon = 'schedule-send';
      bannerTone = 'soon';
    } else if (shareStatus === 'COMPLETED') {
      // Loop complete — lender confirmed receipt. The duplicate row
      // is still on the borrower's account (BE doesn't archive it
      // yet — Phase 2). Banner explains the row's residual presence
      // and PR11 will grey the card on the Items grid.
      bannerTitle = lenderName
        ? `Returned to ${lenderName}`
        : 'Returned';
      bannerSubtitle = 'Receipt confirmed';
      bannerIcon = 'check-circle';
      bannerTone = 'neutral';
    } else {
      // CLAIMED — the original PR9 banner copy.
      bannerTitle = lenderName
        ? `Borrowed from ${lenderName} via share`
        : 'Borrowed via share';
      if (dueInfo.text) bannerSubtitle = dueInfo.text;
      else if (history?.custody_since) {
        bannerSubtitle = `claimed ${formatRelative(history.custody_since)}`;
      }
      bannerIcon = 'inventory';
      if (dueInfo.overdue) bannerTone = 'overdue';
      else if (dueInfo.dueSoon) bannerTone = 'soon';
    }
    bannerCounterpartyUserId = claimedFromUserId ?? null;
  } else if (
    viewerIsOwner &&
    matchedOutboundShare &&
    matchedOutboundShare.status === 'RETURNED'
  ) {
    // PR12 Bug 4 — lender's outbound share is in RETURNED status.
    // The borrower has marked returned, awaiting the owner (viewer)
    // to tap Confirm receipt. Render the prompt + the [Confirm
    // receipt] CTA renders below in the actions block.
    const borrowerName =
      matchedOutboundShare.shared_with
        ? matchedOutboundShare.shared_with.split('(')[0].trim() || null
        : null;
    bannerTitle = borrowerName
      ? `${borrowerName} marked returned`
      : 'Borrower marked returned';
    bannerSubtitle = 'Confirm receipt to close the loop';
    bannerIcon = 'mark-email-read';
    bannerTone = 'soon';
    // SharedByMe carries `shared_with` as "Name (email)" but no
    // user_id. Email is still parseable for the contact row.
  } else if (
    viewerIsOwner &&
    matchedOutboundShare &&
    matchedOutboundShare.status === 'CLAIMED'
  ) {
    // PR12 — lender's outbound share is CLAIMED (item currently with
    // borrower). Replaces the misleading "Currently with you"
    // default. (Bug 5 will fully timeline this in custody history.)
    const borrowerName =
      matchedOutboundShare.shared_with
        ? matchedOutboundShare.shared_with.split('(')[0].trim() || null
        : null;
    bannerTitle = borrowerName
      ? `Lent to ${borrowerName} via share`
      : 'Lent via share';
    bannerSubtitle = matchedOutboundShare.expected_return_date
      ? formatDueLabel(matchedOutboundShare.expected_return_date).text
      : 'In their possession';
    bannerIcon = 'arrow-forward';
    bannerTone = 'neutral';
  } else if (viewerIsOwner && itemWithOwner) {
    bannerTitle = 'Currently with you';
    if (history?.custody_since) {
      bannerSubtitle = `since ${formatRelative(history.custody_since)}`;
    }
  } else if (viewerIsCustodian && !viewerIsOwner) {
    const ownerMember = memberByUserId[ownerUserId];
    // FE-248 v2 follow-up — three-step fallback chain so the borrower
    // ALWAYS sees a real name when one is known anywhere:
    //   1. Cached group-members list (canonical when navigating from
    //      GroupDetail).
    //   2. fallbackOwnerName from GroupItemSummary.owner_name
    //      (denormalized server-side, available even on cold-start
    //      deep-links via the group inventory hydration fallback).
    //   3. Generic "owner" copy (last resort, never lies about a name).
    const resolvedOwnerName = ownerMember
      ? memberLabel(ownerMember)
      : fallbackOwnerName && fallbackOwnerName.trim()
        ? fallbackOwnerName.trim()
        : null;
    bannerTitle = resolvedOwnerName
      ? `Borrowed from ${resolvedOwnerName}`
      : 'Borrowed from owner';
    if (dueInfo.text) bannerSubtitle = dueInfo.text;
    bannerIcon = 'inventory';
    if (dueInfo.overdue) bannerTone = 'overdue';
    else if (dueInfo.dueSoon) bannerTone = 'soon';
    bannerCounterpartyUserId = ownerUserId;
  } else if (viewerIsOwner && !itemWithOwner) {
    const holder = memberByUserId[custodianUserId ?? -1];
    bannerTitle = holder
      ? `With ${memberLabel(holder)}`
      : 'With another member';
    if (dueInfo.text) bannerSubtitle = dueInfo.text;
    bannerIcon = 'person-pin';
    if (dueInfo.overdue) bannerTone = 'overdue';
    else if (dueInfo.dueSoon) bannerTone = 'soon';
    bannerCounterpartyUserId = custodianUserId ?? null;
  } else {
    // Viewer is neither owner nor custodian — likely viewing a crew
    // item they don't currently hold. Render neutral copy.
    const holder = memberByUserId[custodianUserId ?? -1];
    bannerTitle = holder
      ? `With ${memberLabel(holder)}`
      : 'In this circle';
    if (dueInfo.text) bannerSubtitle = dueInfo.text;
    bannerIcon = 'group';
    bannerCounterpartyUserId = custodianUserId ?? null;
  }

  // Resolve the counterparty's email for the banner — same 2-step
  // fallback we use elsewhere: cached crew member, then matched
  // share's `shared_by` parse (which carries "Name (email)").
  const bannerCounterparty =
    bannerCounterpartyUserId != null
      ? memberByUserId[bannerCounterpartyUserId]
      : undefined;
  let bannerCounterpartyEmail: string | null = memberEmail(bannerCounterparty);
  if (!bannerCounterpartyEmail && isShareClaimBorrower && matchedShare?.shared_by) {
    // Extract email from "Name (email@x.com)" tail.
    const m = matchedShare.shared_by.match(/\(([^)]+)\)/);
    if (m && m[1].includes('@')) bannerCounterpartyEmail = m[1].trim();
  }

  /* ── actions ──────────────────────────────────────────────────────── */
  // PR9 / LD2 — share-claim borrower flow takes precedence over the
  // crew-handoff handBack because BE#171 rejects /return for rows
  // without a preceding HANDOFF event.
  // PR10 — only show "Mark returned" when the share is still CLAIMED.
  // After the borrower taps it the share flips to RETURNED and the
  // button must disappear (otherwise a second tap would error out
  // with "share is not in a returnable state").
  const showShareReturn = isShareClaimBorrower && shareStatus === 'CLAIMED';
  // PR12 — when the owner has an active outbound share (item with
  // borrower OR borrower marked returned but not yet physically
  // exchanged), they don't have the item in hand. Suppress any
  // possession-based action (handoff, recall, hand back) until the
  // share is COMPLETED. COMPLETED loops the row back to normal.
  const ownerWithActiveOutbound =
    viewerIsOwner &&
    !!matchedOutboundShare &&
    (matchedOutboundShare.status === 'CLAIMED' ||
      matchedOutboundShare.status === 'RETURNED');
  // Owner-push "Hand off" hidden (2026-06-08) — Borrow (member-pull) covers
  // item transfers; no UI entry point. Kept as a const (referenced below)
  // so restoring = reinstating this computation + the CTA/mount.
  const showHandoff = false;
  const showHandBack =
    !isShareClaimBorrower && viewerIsCustodian && !viewerIsOwner;
  // FE-224 — Acknowledge-recall button for the custodian. Only render
  // when there's an open recall: the latest `RECALL` event in the
  // history is NOT followed by a `RECALL_ACK` or `RETURN`. Backend's
  // `POST /inventory/{id}/recall-acknowledge` returns 404 when there's
  // no open recall, but pre-empting on the FE keeps the button clean.
  // Hand back also closes a recall server-side, so this button is a
  // UX nicety, not the only escape hatch.
  const showAcknowledgeRecall = useMemo(() => {
    if (!showHandBack) return false;
    if (!events || events.length === 0) return false;
    // Events come newest-first from selectCustodyEvents.
    for (const ev of events) {
      const action = (ev as any).action;
      if (action === 'RECALL') return true;
      if (action === 'RECALL_ACK' || action === 'RETURN') return false;
    }
    return false;
  }, [events, showHandBack]);
  // Non-owner viewer who is NOT the current custodian → can request
  // to borrow. Requires a group context so the server can scope the
  // borrow_requests row + check auto_approve_handoff.
  const showRequest =
    !isShareClaimBorrower &&
    !viewerIsOwner &&
    !viewerIsCustodian &&
    !!primaryGroupId;
  // Owner with item OUT (held by someone else) → can recall.
  // Sprint 4 / FE-10.3 — emails the holder + creates a high-priority
  // borrow_requests row. Custody flips when the holder ack's.
  const showRecall =
    !isShareClaimBorrower &&
    !ownerWithActiveOutbound &&
    viewerIsOwner &&
    !itemWithOwner;
  // FE-222 / SG-16 — owner can send a soft reminder when item is out.
  // Same visibility predicate as Recall, but reminder is lower-stakes
  // (no high-priority email, no borrow_requests row) and rate-limited
  // server-side to once per 24h per (owner, item).
  const showRemind = showRecall;
  // PR12 — lender confirms receipt after the borrower has marked
  // returned. Closes the share-claim loop. Calls PATCH /users/shares/{id}
  // with action='COMPLETE' (action enum already supported, see
  // sharingTypes.ts:50).
  const showShareComplete =
    viewerIsOwner &&
    !!matchedOutboundShare &&
    matchedOutboundShare.status === 'RETURNED';

  const handleHandBack = async () => {
    const result = await dispatch(returnItem({ itemId }));
    if (returnItem.fulfilled.match(result)) {
      Toast.show({
        type: 'success',
        text1: 'Returned',
        text2: `${itemName} returned to owner`,
      });
    } else {
      Toast.show({
        type: 'error',
        text1: 'Return failed',
        text2: (result.payload as string) ?? 'Please try again.',
      });
    }
  };

  // FE-224 — explicit recall acknowledgement (custodian-side). Server
  // flips custody back AND closes the RECALL borrow_request row. The
  // owner gets a CUSTODY_RECALL_ACK notification. Per BE-147 spec.
  const [ackPending, setAckPending] = useState(false);
  const handleAcknowledgeRecall = async () => {
    setAckPending(true);
    try {
      const result = await dispatch(acknowledgeRecall({ itemId }) as any);
      if (acknowledgeRecall.fulfilled.match(result)) {
        Toast.show({
          type: 'success',
          text1: 'Recall acknowledged',
          text2: `${itemName} returned to owner`,
        });
      } else {
        Toast.show({
          type: 'error',
          text1: 'Could not acknowledge',
          text2: (result.payload as string) ?? 'Please try again.',
        });
      }
    } finally {
      setAckPending(false);
    }
  };

  // FE-222 / SG-16 — soft nudge to the current custodian. 24h
  // server-side rate-limit; on 429 we render a cooldown timer in the
  // button label so the user can't retry. Optimistically caches the
  // last-reminded-at locally so refreshing the page doesn't accidentally
  // re-enable the button before the server cooldown elapses.
  const [remindPending, setRemindPending] = useState(false);
  const [remindNextAllowedAt, setRemindNextAllowedAt] = useState<string | null>(
    null,
  );
  const remindCooldownActive =
    remindNextAllowedAt != null &&
    new Date(remindNextAllowedAt).getTime() > Date.now();
  const remindCooldownLabel = useMemo(() => {
    if (!remindCooldownActive || !remindNextAllowedAt) return null;
    const ms = new Date(remindNextAllowedAt).getTime() - Date.now();
    const hours = Math.max(1, Math.round(ms / 3_600_000));
    return `Reminded · next in ${hours}h`;
  }, [remindCooldownActive, remindNextAllowedAt]);
  const handleRemind = async () => {
    if (remindCooldownActive || remindPending) return;
    setRemindPending(true);
    try {
      const result = await dispatch(remindCustodian({ itemId }) as any);
      if (remindCustodian.fulfilled.match(result)) {
        setRemindNextAllowedAt(result.payload.nextAllowedAt);
        // FE #204 — BE can return days_held=null when custody just started
        // today (or in some race conditions). Render the days suffix only
        // when we have a real value. Avoid "held undefined days".
        const daysHeld = result.payload.daysHeld;
        const heldSuffix =
          typeof daysHeld === 'number' && daysHeld > 0
            ? ` — held ${daysHeld}d`
            : '';
        Toast.show({
          type: 'success',
          text1: 'Reminder sent',
          text2: `${itemName}${heldSuffix}`,
        });
      } else {
        const payload = result.payload as
          | { detail: string; nextAllowedAt?: string }
          | undefined;
        if (payload?.nextAllowedAt) {
          // 429 — server says cooldown active. Cache it so the button
          // shows the timer immediately.
          setRemindNextAllowedAt(payload.nextAllowedAt);
        }
        // Gap #106 (2026-05-27 audit) — the BE detail ("Only one
        // reminder per 24 hours. Last sent 1.2h ago.") was just long
        // enough to truncate mid-word on the 390px toast container
        // ("…1.2h a…"). Compute a shorter, complete sentence
        // ("Try again in 22.8h") that fits, and surface the
        // actionable piece (when they can retry).
        //
        // Sources tried in order, falling through to a safe generic
        // message if none produce a value:
        //   1. `nextAllowedAt` ISO on the rejected payload (BE may
        //      attach it; field is optional)
        //   2. Parse "Last sent X.Yh ago" out of `detail` and compute
        //      `24 - X.Y` for the time-until-next-allowed
        //   3. Hard fallback: "Try again later."
        let text2 = 'Try again later.';
        let hoursUntilNext: number | null = null;
        if (payload?.nextAllowedAt) {
          hoursUntilNext = Math.max(
            0,
            (new Date(payload.nextAllowedAt).getTime() - Date.now()) /
              3_600_000,
          );
        } else if (payload?.detail) {
          const m = payload.detail.match(
            /Last sent\s+(\d+(?:\.\d+)?)\s*h\s+ago/i,
          );
          if (m) {
            const hoursSinceLast = parseFloat(m[1]);
            if (!Number.isNaN(hoursSinceLast)) {
              hoursUntilNext = Math.max(0, 24 - hoursSinceLast);
            }
          }
        }
        if (hoursUntilNext != null) {
          if (hoursUntilNext >= 1) {
            text2 = `Try again in ${hoursUntilNext.toFixed(1)}h.`;
          } else {
            const minutesUntilNext = Math.max(
              1,
              Math.round(hoursUntilNext * 60),
            );
            text2 = `Try again in ${minutesUntilNext} min.`;
          }
        }
        Toast.show({
          type: 'error',
          text1: 'Reminder rate-limited',
          text2,
        });
      }
    } finally {
      setRemindPending(false);
    }
  };

  // PR9 — share-claim "Mark returned" calls the share-management
  // endpoint instead of /inventory/{id}/return. The lifecycle for a
  // share-claim is share.status=CLAIMED → RETURNED, NOT a custody
  // event. The original item row on the lender's side stays put;
  // the share moves to RETURNED + their /users/shares cache reflects
  // the new state on next pull.
  const [shareReturnPending, setShareReturnPending] = useState(false);
  const handleShareReturn = async () => {
    if (claimedFromShareId == null) return;
    setShareReturnPending(true);
    try {
      const result = await dispatch(
        manageShare({
          shareId: claimedFromShareId,
          request: { action: 'RETURN' },
        }) as any,
      );
      if (manageShare.fulfilled.match(result)) {
        Toast.show({
          type: 'success',
          text1: 'Marked returned',
          text2: `${itemName} marked returned to lender.`,
        });
      } else {
        Toast.show({
          type: 'error',
          text1: "Couldn't mark returned",
          text2:
            ((result.payload as any)?.data?.detail as string) ??
            'Please try again.',
        });
      }
    } finally {
      setShareReturnPending(false);
    }
  };

  // PR12 — lender confirms the borrower's return. Calls PATCH
  // /users/shares/{id} with action='COMPLETE'. After fulfillment the
  // share status flips to COMPLETED in Redux, the banner re-renders
  // to neutral, and (eventually, BE work) the borrower's duplicate
  // row is archived. For now PR11 greys the borrower's row.
  const [shareCompletePending, setShareCompletePending] = useState(false);
  const handleShareComplete = async () => {
    if (!matchedOutboundShare) return;
    setShareCompletePending(true);
    try {
      const result = await dispatch(
        manageShare({
          shareId: matchedOutboundShare.share_id,
          request: { action: 'COMPLETE' },
        }) as any,
      );
      if (manageShare.fulfilled.match(result)) {
        Toast.show({
          type: 'success',
          text1: 'Receipt confirmed',
          text2: `${itemName} share closed.`,
        });
      } else {
        Toast.show({
          type: 'error',
          text1: "Couldn't confirm receipt",
          text2:
            ((result.payload as any)?.data?.detail as string) ??
            'Please try again.',
        });
      }
    } finally {
      setShareCompletePending(false);
    }
  };

  /* ── history ──────────────────────────────────────────────────────── */
  // PR9 follow-up + PR10 + PR13 — synthesize virtual events to bridge
  // the backend gap (BE#177 / BE#178 will write real events to
  // item_custody_event on share-claim / share-return — until then
  // these placeholders make the timeline coherent on both sides).
  //
  // Two parallel flows, mutually exclusive per item:
  //   • Borrower side (isShareClaimBorrower) — synth events on the
  //     duplicated row. Three possible: claim / marked returned /
  //     receipt confirmed. PR9.1 + PR10 shipped these.
  //   • Lender side (matchedOutboundShare + viewerIsOwner) — PR13.
  //     Same three events from the owner's perspective: lent out /
  //     borrower marked returned / receipt confirmed.
  //
  // IDs use distinct negative spaces so the lifecycle is unambiguous:
  //   -1/-2/-3   → borrower-side claim/return/confirm
  //   -11/-12/-13 → lender-side lend/return-marked/confirm
  //
  // Skip ALL synthetics if real custody events exist (BE#177/178
  // shipped) — real events win.
  const claimedAtIso =
    (matchedShare as any)?.claimed_at ??
    (history?.custody_since as string | undefined) ??
    null;
  const returnedAtIso = (matchedShare as any)?.returned_at ?? null;
  const completedAtIso = (matchedShare as any)?.completed_at ?? null;

  // Lender-side timestamps from the matched outbound share. Pull
  // from the LIFECYCLE variant so COMPLETED shares (which the
  // narrower matchedOutboundShare excludes) still contribute their
  // event timeline. Same "now" fallback when BE hasn't shipped
  // returned_at/completed_at yet — keeps chronological order.
  const outboundCreatedIso = matchedOutboundShareLifecycle?.created_at ?? null;
  const outboundClaimedIso = matchedOutboundShareLifecycle?.claimed_at ?? null;
  const outboundReturnedIso =
    (matchedOutboundShareLifecycle as any)?.returned_at ?? null;
  const outboundCompletedIso =
    (matchedOutboundShareLifecycle as any)?.completed_at ?? null;
  const outboundStatus = matchedOutboundShareLifecycle?.status;

  // PR17 — Gap-fill synthesis. Previously this was all-or-nothing
  // (events.length > 0 → no synth). That broke as BE#177/#178
  // started landing partial coverage: if BE wrote the claim but
  // not the return, the FE would render just the claim and never
  // synthesize the missing steps. Now we look at WHICH lifecycle
  // steps are already covered by real events and only synthesize
  // the gaps.
  //
  // "Coverage" is detected by matching the action enum:
  //   step  | real action(s) that cover it
  //   ------|--------------------------------------
  //   claim | HANDOFF, LEND_OUT, CLAIM (BE may emit any)
  //   return| RETURN, LEND_BACK
  //   confirm| RECONCILE, COMPLETE
  //
  // For share-claim duplicates the row has no crew context so any
  // HANDOFF event there can only be the share-claim itself. For the
  // lender's original row, a real HANDOFF could be either a share
  // event or a crew event — but if BE didn't write one and the
  // outbound share is CLAIMED+, we should still synthesize the
  // "lent via share" event so the user sees the lifecycle.
  const syntheticShareEvents = useMemo<CustodyEventSummary[]>(() => {
    const realActions = new Set(events.map((e) => e.action));
    const hasClaimEvent =
      realActions.has('HANDOFF') ||
      realActions.has('LEND_OUT') ||
      realActions.has('CLAIM' as any);
    const hasReturnEvent =
      realActions.has('RETURN') || realActions.has('LEND_BACK');
    const hasConfirmEvent =
      realActions.has('RECONCILE') || realActions.has('COMPLETE' as any);

    const out: CustodyEventSummary[] = [];

    // ── Borrower side ───────────────────────────────────────────────
    if (isShareClaimBorrower && claimedAtIso) {
      if (!hasClaimEvent) {
        out.push({
          id: -1,
          action: 'HANDOFF' as any,
          actor_user_id: (claimedFromUserId ?? 0) as number,
          from_user_id: (claimedFromUserId ?? null) as number | null,
          to_user_id: currentUser?.id ?? null,
          due_date: null,
          condition_photo_url: null,
          note: 'Claimed via share',
          created_at: claimedAtIso,
        } as any);
      }
      if (
        (shareStatus === 'RETURNED' || shareStatus === 'COMPLETED') &&
        !hasReturnEvent
      ) {
        out.push({
          id: -2,
          action: 'RETURN' as any,
          actor_user_id: (currentUser?.id ?? 0) as number,
          from_user_id: currentUser?.id ?? null,
          to_user_id: (claimedFromUserId ?? null) as number | null,
          due_date: null,
          condition_photo_url: null,
          note: 'Marked returned',
          created_at: returnedAtIso ?? new Date().toISOString(),
        } as any);
      }
      if (shareStatus === 'COMPLETED' && !hasConfirmEvent) {
        out.push({
          id: -3,
          action: 'RECONCILE' as any,
          actor_user_id: (claimedFromUserId ?? 0) as number,
          from_user_id: (claimedFromUserId ?? null) as number | null,
          to_user_id: (claimedFromUserId ?? null) as number | null,
          due_date: null,
          condition_photo_url: null,
          note: 'Receipt confirmed by lender',
          created_at: completedAtIso ?? new Date().toISOString(),
        } as any);
      }
      return out;
    }

    // ── Lender side ─────────────────────────────────────────────────
    if (
      viewerIsOwner &&
      matchedOutboundShareLifecycle &&
      (outboundStatus === 'CLAIMED' ||
        outboundStatus === 'RETURNED' ||
        outboundStatus === 'COMPLETED')
    ) {
      if (!hasClaimEvent) {
        out.push({
          id: -11,
          action: 'LEND_OUT' as any,
          actor_user_id: (currentUser?.id ?? 0) as number,
          from_user_id: currentUser?.id ?? null,
          to_user_id: null, // SharedByMe lacks user_id; name in note
          due_date:
            matchedOutboundShareLifecycle.expected_return_date ?? null,
          condition_photo_url: null,
          note: 'Lent via share',
          created_at:
            outboundClaimedIso ?? outboundCreatedIso ?? new Date().toISOString(),
        } as any);
      }
      if (
        (outboundStatus === 'RETURNED' || outboundStatus === 'COMPLETED') &&
        !hasReturnEvent
      ) {
        out.push({
          id: -12,
          action: 'LEND_BACK' as any,
          actor_user_id: null as any,
          from_user_id: null,
          to_user_id: currentUser?.id ?? null,
          due_date: null,
          condition_photo_url: null,
          note: 'Borrower marked returned',
          created_at: outboundReturnedIso ?? new Date().toISOString(),
        } as any);
      }
      if (outboundStatus === 'COMPLETED' && !hasConfirmEvent) {
        out.push({
          id: -13,
          action: 'RECONCILE' as any,
          actor_user_id: (currentUser?.id ?? 0) as number,
          from_user_id: currentUser?.id ?? null,
          to_user_id: currentUser?.id ?? null,
          due_date: null,
          condition_photo_url: null,
          note: 'You confirmed receipt',
          created_at: outboundCompletedIso ?? new Date().toISOString(),
        } as any);
      }
      return out;
    }

    return [];
  }, [
    events,
    isShareClaimBorrower,
    claimedAtIso,
    returnedAtIso,
    completedAtIso,
    claimedFromUserId,
    currentUser?.id,
    shareStatus,
    viewerIsOwner,
    matchedOutboundShareLifecycle,
    outboundStatus,
    outboundCreatedIso,
    outboundClaimedIso,
    outboundReturnedIso,
    outboundCompletedIso,
  ]);

  // PR17 — merge real + synth, sort newest-first. Previously synth
  // shadowed real entirely; now they coexist so partial BE coverage
  // still produces a complete timeline.
  const displayEvents = useMemo(() => {
    const all = [...events, ...syntheticShareEvents];
    return all.sort((a, b) => {
      const at = new Date(a.created_at).getTime();
      const bt = new Date(b.created_at).getTime();
      return bt - at; // descending — newest first
    });
  }, [syntheticShareEvents, events]);
  const visibleEvents = expanded
    ? displayEvents
    : displayEvents.slice(0, PREVIEW_COUNT);
  const hasMore = displayEvents.length > PREVIEW_COUNT;

  const bannerStyle = [
    styles.banner,
    bannerTone === 'overdue' && styles.bannerOverdue,
    bannerTone === 'soon' && styles.bannerSoon,
  ];

  // Sprint 8 Phase E (Issue 4b fix) — hide the "Currently with you"
  // banner when the viewer is the owner AND holds the item AND there's
  // no urgency (no due-soon, no overdue). The label has zero info
  // value for the owner — they already know they own and have it.
  // Keep the banner visible when:
  //   - viewer is the custodian but NOT the owner → "Borrowed from X"
  //   - viewer is the owner but item is OUT → "With X, due Y"
  //   - any tone other than 'neutral' (overdue, due-soon)
  //   - history exists (some custody event has happened — context matters)
  //   - PR9: viewer is a share-claim borrower → "Borrowed from X via share"
  //   - PR12: viewer is a lender with an outbound share in CLAIMED or
  //     RETURNED status → "Lent to X / X marked returned" — high
  //     signal even though the row's custodian is still the lender.
  const showBanner =
    !(
      viewerIsOwner &&
      itemWithOwner &&
      bannerTone === 'neutral' &&
      events.length === 0 &&
      !isShareClaimBorrower &&
      !matchedOutboundShare
    );

  return (
    <View style={styles.section} testID="custody-section">
      {/* Banner — hidden when there's no useful info to show */}
      {showBanner && <View
        style={bannerStyle}
        accessible
        accessibilityRole="text"
        accessibilityLabel={`${bannerTitle}${bannerSubtitle ? ', ' + bannerSubtitle : ''}`}
      >
        <View
          style={[
            styles.bannerIconWrap,
            bannerTone === 'overdue' && styles.bannerIconWrapOverdue,
            bannerTone === 'soon' && styles.bannerIconWrapSoon,
          ]}
        >
          <MaterialIcons
            name={bannerIcon}
            size={20}
            color={
              bannerTone === 'overdue'
                ? fpColors.error
                : bannerTone === 'soon'
                ? fpColors.tertiary
                : fpColors.primary
            }
          />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.bannerTitle} testID="custody-banner-title">
            {bannerTitle}
          </Text>
          {bannerSubtitle ? (
            <Text
              testID="custody-banner-subtitle"
              style={[
                styles.bannerSubtitle,
                bannerTone === 'overdue' && styles.bannerSubtitleOverdue,
              ]}
            >
              {bannerSubtitle}
            </Text>
          ) : null}
          {bannerCounterpartyEmail ? (
            <Pressable
              onPress={() => openMailto(bannerCounterpartyEmail!)}
              hitSlop={6}
              accessibilityRole="button"
              accessibilityLabel={`Email ${bannerCounterparty ? memberLabel(bannerCounterparty) : 'counterparty'} at ${bannerCounterpartyEmail}`}
              testID="custody-banner-mailto"
              style={styles.bannerContactRow}
            >
              <MaterialIcons
                name="mail-outline"
                size={12}
                color={fpColors.primary}
              />
              {/* FE-203 follow-up — When we have the counterparty's
                  name resolved (it's already in the bannerTitle right
                  above this row, e.g. "Borrowed from Marcus Reference"),
                  surface "Email" as the visible text instead of the
                  full address. Keeps the mailto affordance + the full
                  email in the aria-label for screen readers, but stops
                  exposing the address as PII in the page flow. Falls
                  through to the raw email only when no name resolved
                  (share-claim from a non-crew lender). */}
              <Text style={styles.bannerContactEmail} numberOfLines={1}>
                {bannerCounterparty ? 'Email' : bannerCounterpartyEmail}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </View>}

      {/* Actions */}
      {(showHandoff || showHandBack || showShareReturn || showRequest || showRecall || showRemind || showShareComplete || showAcknowledgeRecall) && (
        <View style={styles.actions}>
          {showShareComplete ? (
            <Pressable
              onPress={handleShareComplete}
              disabled={shareCompletePending}
              style={[
                styles.actionRow,
                shareCompletePending && styles.actionRowDisabled,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Confirm receipt — close the share loop"
              testID="custody-share-complete-btn"
            >
              <MaterialIcons
                name="mark-email-read"
                size={20}
                color={fpColors.primary}
              />
              <Text style={styles.actionLabel}>
                {shareCompletePending ? 'Confirming…' : 'Confirm receipt'}
              </Text>
              <MaterialIcons
                name="chevron-right"
                size={22}
                color={fpColors.outline}
              />
            </Pressable>
          ) : null}
          {/* Owner-push "Hand off to circle member" CTA removed
              (2026-06-08) — feature hidden; Borrow covers transfers. */}
          {showShareReturn ? (
            <Pressable
              onPress={handleShareReturn}
              disabled={shareReturnPending}
              style={[
                styles.actionRow,
                shareReturnPending && styles.actionRowDisabled,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Mark item returned to lender"
              testID="custody-share-return-btn"
            >
              <MaterialIcons
                name="check-circle-outline"
                size={20}
                color={fpColors.primary}
              />
              <Text style={styles.actionLabel}>
                {shareReturnPending ? 'Marking returned…' : 'Mark returned'}
              </Text>
              <MaterialIcons
                name="chevron-right"
                size={22}
                color={fpColors.outline}
              />
            </Pressable>
          ) : null}
          {/* FE-224 — explicit Acknowledge button when an active RECALL
              exists on this item. Optional path; Hand back also closes
              the recall server-side, but Ack lets the custodian confirm
              they got the message without committing to physically
              returning right this second. */}
          {showAcknowledgeRecall ? (
            <Pressable
              onPress={handleAcknowledgeRecall}
              disabled={ackPending}
              style={[
                styles.actionRow,
                ackPending && styles.actionRowDisabled,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Acknowledge recall from owner"
              testID="custody-ack-recall-btn"
            >
              <MaterialIcons
                name="check-circle"
                size={20}
                color={fpColors.error}
              />
              <Text style={[styles.actionLabel, { color: fpColors.error }]}>
                {ackPending ? 'Acknowledging…' : 'Acknowledge recall'}
              </Text>
              <MaterialIcons
                name="chevron-right"
                size={22}
                color={fpColors.outline}
              />
            </Pressable>
          ) : null}
          {showHandBack ? (
            <Pressable
              onPress={handleHandBack}
              disabled={loading.return}
              style={[styles.actionRow, loading.return && styles.actionRowDisabled]}
              accessibilityRole="button"
              accessibilityLabel="Hand back to owner"
              testID="custody-handback-btn"
            >
              <MaterialIcons
                name="check-circle-outline"
                size={20}
                color={fpColors.primary}
              />
              <Text style={styles.actionLabel}>
                {loading.return ? 'Returning…' : 'Hand back to owner'}
              </Text>
              <MaterialIcons
                name="chevron-right"
                size={22}
                color={fpColors.outline}
              />
            </Pressable>
          ) : null}
          {showRequest ? (
            <Pressable
              onPress={() => setRequestOpen(true)}
              style={styles.actionRow}
              accessibilityRole="button"
              accessibilityLabel="Ask to borrow this item"
              testID="custody-request-btn"
            >
              <MaterialIcons
                name="pan-tool"
                size={20}
                color={fpColors.primary}
              />
              <Text style={styles.actionLabel}>Ask to borrow</Text>
              <MaterialIcons
                name="chevron-right"
                size={22}
                color={fpColors.outline}
              />
            </Pressable>
          ) : null}
          {showRemind ? (
            <Pressable
              onPress={handleRemind}
              disabled={remindPending || remindCooldownActive}
              style={[
                styles.actionRow,
                (remindPending || remindCooldownActive) && { opacity: 0.55 },
              ]}
              accessibilityRole="button"
              accessibilityLabel={
                remindCooldownLabel ?? 'Send a reminder to the current holder'
              }
              testID="custody-remind-btn"
            >
              <MaterialIcons
                name="notifications-active"
                size={20}
                color={fpColors.primary}
              />
              <Text style={[styles.actionLabel, { color: fpColors.primary }]}>
                {remindCooldownLabel ?? 'Send reminder'}
              </Text>
              <MaterialIcons
                name="chevron-right"
                size={22}
                color={fpColors.outline}
              />
            </Pressable>
          ) : null}
          {showRecall ? (() => {
            // FE #155 — when has_active_recall=true the recall action
            // flips to a pending/locked state. Server already rate-
            // limits the endpoint, but the UI needs to reflect the
            // pending state so the owner doesn't think their first
            // tap was a no-op + try again.
            const holder = memberByUserId[custodianUserId ?? -1];
            const holderName = holder ? memberLabel(holder) : null;
            return (
              <Pressable
                onPress={hasActiveRecall ? undefined : () => setRecallOpen(true)}
                disabled={hasActiveRecall}
                style={[
                  styles.actionRow,
                  hasActiveRecall && { opacity: 0.55 },
                ]}
                accessibilityRole="button"
                accessibilityLabel={
                  hasActiveRecall
                    ? `Recall pending — awaiting ${holderName ?? 'the current holder'}`
                    : 'Recall item from current holder'
                }
                accessibilityState={{ disabled: hasActiveRecall }}
                testID="custody-recall-btn"
              >
                <MaterialIcons
                  name={hasActiveRecall ? 'hourglass-empty' : 'priority-high'}
                  size={20}
                  color={fpColors.error}
                />
                <Text style={[styles.actionLabel, { color: fpColors.error }]}>
                  {hasActiveRecall
                    ? `Recall pending · awaiting ${holderName ?? 'reply'}`
                    : 'Recall item'}
                </Text>
                <MaterialIcons
                  name="chevron-right"
                  size={22}
                  color={fpColors.outline}
                />
              </Pressable>
            );
          })() : null}
        </View>
      )}

      {/* PR17 — Custody history card. Card chrome + timeline rail +
          3-line row body. Mirrors LocationHistoryTimeline's visual
          language so the two timelines on ItemDetail feel unified.

          The "Claimed from Kanishk Patel (kanishk@remembr.xyz)"
          description on the claim row is the headline payoff users
          explicitly asked for. */}
      <View style={styles.historyCard} testID="custody-history-card">
        <View style={styles.historyHeader}>
          <MaterialIcons
            name="history"
            size={18}
            color={fpColors.primary}
          />
          {/* FE-206 — renamed from "Custody history" to read more
              naturally for non-technical users. Pairs with FE-205's
              Borrowed/Lent Out vocabulary shift across the app. */}
          <Text style={styles.historyTitle}>Borrowed history</Text>
          {displayEvents.length > 0 ? (
            <View style={styles.historyCountPill}>
              <Text style={styles.historyCountPillText}>
                {displayEvents.length}
              </Text>
            </View>
          ) : null}
        </View>

        {loading.history && displayEvents.length === 0 ? (
          <View style={styles.historyLoading}>
            <ActivityIndicator size="small" color={fpColors.primary} />
          </View>
        ) : displayEvents.length === 0 ? (
          <Text style={styles.historyEmpty}>No handoffs yet.</Text>
        ) : (
          <View style={styles.historyTimeline}>
            {visibleEvents.map((ev, idx) => {
              const desc = describeCustodyAction(ev);
              const isLast = idx === visibleEvents.length - 1;

              // PR18a — Counterparty = "the other person on this row".
              // The viewer's own user_id must NEVER resolve to the
              // counterparty, otherwise lender-side synth events (-11
              // "Lent via share" has from=viewer, to=null because
              // SharedByMe doesn't expose the borrower's user_id) fall
              // back to the viewer's cached record and surface their
              // own name/email as the "counterparty." This was the
              // root cause of the "Lent to {viewer} · {viewer email}"
              // bug seen in production.
              //
              // Resolution order (skipping self):
              //   1. to_user_id when it's set AND not the viewer
              //   2. from_user_id when it's set AND not the viewer
              //   3. null → fall through to the matched-share parse
              //      block below for synth events
              const viewerId = currentUser?.id;
              let counterpartyId: number | null = null;
              if (ev.to_user_id != null && ev.to_user_id !== viewerId) {
                counterpartyId = ev.to_user_id;
              } else if (
                ev.from_user_id != null &&
                ev.from_user_id !== viewerId
              ) {
                counterpartyId = ev.from_user_id;
              }
              const counterparty = counterpartyId
                ? memberByUserId[counterpartyId]
                : undefined;
              let counterpartyName = counterparty
                ? memberLabel(counterparty)
                : null;
              let contactEmail = memberEmail(counterparty);

              // For share-claim synthetic events, the lender's name
              // is often not in the crew cache. Parse "Name (email)"
              // out of the matched share (PR9.1 + PR9.2 pattern).
              const synthId = (ev as any).id as number;
              const isBorrowerSynth =
                synthId === -1 || synthId === -2 || synthId === -3;
              const isLenderSynth =
                synthId === -11 || synthId === -12 || synthId === -13;
              if (!counterpartyName && isBorrowerSynth && matchedShare?.shared_by) {
                counterpartyName =
                  matchedShare.shared_by.split('(')[0].trim() || null;
                if (!contactEmail) {
                  const m = matchedShare.shared_by.match(/\(([^)]+)\)/);
                  if (m && m[1].includes('@')) contactEmail = m[1].trim();
                }
              }
              if (
                !counterpartyName &&
                isLenderSynth &&
                matchedOutboundShareLifecycle?.shared_with
              ) {
                counterpartyName =
                  matchedOutboundShareLifecycle.shared_with
                    .split('(')[0]
                    .trim() || null;
                if (!contactEmail) {
                  const m =
                    matchedOutboundShareLifecycle.shared_with.match(
                      /\(([^)]+)\)/,
                    );
                  if (m && m[1].includes('@')) contactEmail = m[1].trim();
                }
              }

              // Compose the human-friendly description line. Synth
              // events use a directional phrasing; real BE events
              // fall back to the note (if any).
              let description: string | null = null;
              if (synthId === -1) {
                description = counterpartyName
                  ? `Claimed from ${counterpartyName}`
                  : 'Claimed via share';
              } else if (synthId === -2) {
                description = counterpartyName
                  ? `Returned to ${counterpartyName}`
                  : 'Marked returned';
              } else if (synthId === -3) {
                description = 'Loop closed by lender';
              } else if (synthId === -11) {
                description = counterpartyName
                  ? `Lent to ${counterpartyName}`
                  : 'Lent via share';
              } else if (synthId === -12) {
                description = counterpartyName
                  ? `${counterpartyName} marked returned`
                  : 'Borrower marked returned';
              } else if (synthId === -13) {
                description = 'You closed the loop';
              } else {
                // Real BE event — show note when present, otherwise
                // a directional phrase derived from the counterparty.
                if (ev.note) {
                  description = ev.note;
                } else if (counterpartyName) {
                  // FE #157 — copy must flip per viewer. The same
                  // RETURN event reads differently for owner vs
                  // borrower:
                  //   Marcus's view (owner):    "Returned from Blair"
                  //   Blair's view (borrower):  "Returned to Marcus"
                  // Same with HANDOFF:
                  //   Marcus's view:   "Handed to Blair"
                  //   Blair's view:    "Received from Marcus"
                  // Same with REMINDER:
                  //   Marcus's view:   "Reminder sent to Blair"
                  //   Blair's view:    "Reminder received from Marcus"
                  const isReturnAction =
                    ev.action === 'RETURN' ||
                    ev.action === 'LEND_BACK' ||
                    ev.action === 'RECALL_ACK';
                  const isReminderAction =
                    ev.action === 'REMINDER' || ev.action === 'NUDGE';
                  if (isReturnAction) {
                    description = viewerIsOwner
                      ? `Returned from ${counterpartyName}`
                      : `Returned to ${counterpartyName}`;
                  } else if (isReminderAction) {
                    description = viewerIsOwner
                      ? `Reminder sent to ${counterpartyName}`
                      : `Reminder received from ${counterpartyName}`;
                  } else {
                    // HANDOFF + default
                    description = viewerIsOwner
                      ? `Handed to ${counterpartyName}`
                      : `Received from ${counterpartyName}`;
                  }
                }
              }

              const rowInner = (
                <>
                  <View style={styles.historyRail}>
                    <View
                      style={[styles.historyDot, { backgroundColor: desc.tint }]}
                    >
                      <MaterialIcons
                        name={desc.icon}
                        size={11}
                        color="#FFFFFF"
                      />
                    </View>
                    {!isLast ? <View style={styles.historyLine} /> : null}
                  </View>
                  <View style={styles.historyRowBody}>
                    <View style={styles.historyHeaderLine}>
                      <Text style={styles.historyTimestamp}>
                        {formatTimestamp(ev.created_at)}
                      </Text>
                      {counterpartyName ? (
                        <Text style={styles.historyActor}>
                          · {counterpartyName}
                        </Text>
                      ) : null}
                    </View>
                    <Text style={[styles.historySource, { color: desc.tint }]}>
                      {desc.label}
                    </Text>
                    {description ? (
                      <Text style={styles.historyDescription} numberOfLines={2}>
                        {description}
                      </Text>
                    ) : null}
                    {/* FE-203 — Email address used to render as a visible
                        line on EVERY event row, exposing the counterparty's
                        full address 6+ times per page on busy items. With
                        a named counterparty, the mailto affordance is the
                        envelope icon at the right edge of the row — that's
                        plenty. We only render the email inline as a fallback
                        when there's no name to anchor to (rare; usually
                        the share-claim path that has email-only context).
                        The full row stays tappable / mailto-able when
                        `contactEmail` is set; just no PII spam in the
                        page flow. */}
                    {contactEmail && !counterpartyName ? (
                      <Text style={styles.historyEmail} numberOfLines={1}>
                        {contactEmail}
                      </Text>
                    ) : null}
                  </View>
                  {contactEmail ? (
                    <View style={styles.historyMailIcon}>
                      <MaterialIcons
                        name="mail-outline"
                        size={14}
                        color={fpColors.outline}
                      />
                    </View>
                  ) : null}
                </>
              );

              return contactEmail ? (
                <Pressable
                  key={ev.id}
                  onPress={() => openMailto(contactEmail!)}
                  hitSlop={4}
                  style={styles.historyRow}
                  accessibilityRole="button"
                  accessibilityLabel={`Email ${counterpartyName ?? 'counterparty'} at ${contactEmail}`}
                  testID={`custody-event-${ev.id}`}
                >
                  {rowInner}
                </Pressable>
              ) : (
                <View
                  key={ev.id}
                  style={styles.historyRow}
                  testID={`custody-event-${ev.id}`}
                >
                  {rowInner}
                </View>
              );
            })}
            {hasMore ? (
              <Pressable
                onPress={() => setExpanded((v) => !v)}
                style={styles.expandBtn}
                accessibilityRole="button"
                accessibilityLabel={
                  expanded ? 'Show fewer events' : 'Show all events'
                }
                testID="custody-history-toggle"
              >
                <Text style={styles.expandText}>
                  {expanded
                    ? 'Show fewer'
                    : `Show all ${displayEvents.length} events`}
                </Text>
                <MaterialIcons
                  name={expanded ? 'expand-less' : 'expand-more'}
                  size={16}
                  color={fpColors.primary}
                />
              </Pressable>
            ) : null}
          </View>
        )}
      </View>

      {/* Hand-off sheet (owner-push) removed from the tree (2026-06-08) —
          feature hidden; HandOffSheet.tsx kept dormant. */}

      {/* Request handoff sheet (member-pull "Ask to borrow") */}
      {primaryGroupId ? (
        <RequestHandoffSheet
          visible={requestOpen}
          itemId={itemId}
          itemName={itemName}
          groupId={primaryGroupId}
          ownerUserId={ownerUserId}
          onClose={() => setRequestOpen(false)}
        />
      ) : null}

      {/* Recall sheet — owner urgent pull-back. Visible only when the
          item is out with someone else; the sheet sends a high-priority
          email + creates a borrow_requests row. */}
      <RecallSheet
        visible={recallOpen}
        itemId={itemId}
        itemName={itemName}
        custodianUserId={custodianUserId}
        onClose={() => setRecallOpen(false)}
      />
    </View>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  section: {
    marginTop: 20,
    marginBottom: 8,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 12,
  },
  bannerSoon: {
    backgroundColor: fpColors.tertiaryFixed,
  },
  bannerOverdue: {
    backgroundColor: fpColors.errorContainer,
  },
  bannerIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.primaryContainer,
  },
  bannerIconWrapSoon: {
    backgroundColor: fpColors.tertiaryFixedDim,
  },
  bannerIconWrapOverdue: {
    backgroundColor: '#FFCDC8',
  },
  bannerTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  bannerSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.outline,
    marginTop: 2,
  },
  bannerSubtitleOverdue: {
    color: fpColors.error,
    fontFamily: 'Inter_600SemiBold',
  },
  // PR9.2 — small tappable email line under the banner subtitle.
  // Lives just under "due in 4 days" / "claimed 22h ago" so the
  // counterparty's contact info is one tap away.
  bannerContactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  bannerContactEmail: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.primary,
    textDecorationLine: 'underline',
  },
  actions: {
    marginTop: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 12,
    overflow: 'hidden',
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 10,
  },
  actionRowDisabled: {
    opacity: 0.6,
  },
  actionLabel: {
    flex: 1,
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  // PR17 — Custody history card, redesigned to match the look of
  // LocationHistoryTimeline. Card chrome + timeline rail + 3-line
  // row body. Style names mirror LocationHistoryTimeline so the two
  // surfaces feel unified.
  historyCard: {
    marginTop: 18,
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 10,
  },
  historyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  historyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    flex: 1,
  },
  historyCountPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: fpColors.primaryContainer,
  },
  historyCountPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onPrimaryContainer,
  },
  historyLoading: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  historyEmpty: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    paddingVertical: 8,
  },
  // Vertical stack of timeline rows.
  historyTimeline: { gap: 14 },
  // One row = [rail][body].
  historyRow: { flexDirection: 'row', gap: 10 },
  // Left rail: colored dot + vertical line connecting to the next row.
  historyRail: {
    width: 22,
    alignItems: 'center',
  },
  historyDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  historyLine: {
    flex: 1,
    width: 2,
    backgroundColor: fpColors.outlineVariant,
    marginTop: 2,
  },
  // Right body: stack of 3 lines (timestamp · actor / SOURCE / desc).
  historyRowBody: { flex: 1, paddingBottom: 4, gap: 2 },
  historyHeaderLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  historyTimestamp: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onSurface,
  },
  historyActor: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  historySource: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  historyDescription: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  historyEmail: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.primary,
    textDecorationLine: 'underline',
  },
  historyMailIcon: {
    alignSelf: 'flex-start',
    paddingTop: 2,
  },
  expandBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  expandText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
});

export default CustodySection;
